import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Typography, Alert, CircularProgress } from "@mui/material";
import { AuctionForm } from "../components/exchange/auction";
import auctionService from "../services/auctionService";
import type { AuctionFormValues } from "../components/exchange/auction/AuctionForm";
import gisService from "../services/gisService";
import { GISProperty } from "../types/gis";

const EditAuctionAdmin: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [item, setItem] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [gisProperties, setGisProperties] = useState<GISProperty[]>([]);

  useEffect(() => {
    const fetchItemAndGis = async () => {
      if (!id) {
        setError("No auction ID provided");
        setLoading(false);
        return;
      }
      try {
        const auctionPromise = auctionService.getLiveAuction(id);
        const gisPromise = gisService.getGisProperties();

        const [auctionResponse, gisResponse] = await Promise.all([
          auctionPromise,
          gisPromise.catch((e) => {
            console.error(
              "Failed to load GIS properties, continuing without them.",
              e
            );
            return []; // Return empty array on failure, so page doesn't crash
          }),
        ]);

        if (!auctionResponse) {
          throw new Error("Auction data could not be loaded.");
        }

        setItem(auctionResponse);
        if (Array.isArray(gisResponse)) {
          setGisProperties(gisResponse);
        } else {
          console.error(
            "GIS properties response was not an array:",
            gisResponse
          );
          setGisProperties([]); // Fallback to an empty array
        }
      } catch (err: any) {
        setError(err?.message || "Failed to load auction data.");
      } finally {
        setLoading(false);
      }
    };
    fetchItemAndGis();
  }, [id]);

  const handleSubmit = async (
    values: AuctionFormValues,
    videoFile?: File | null
  ) => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append("title", values.title);
      formData.append("description", values.description);
      formData.append("starting_price", values.starting_price.toString());
      formData.append("min_increment", values.min_increment.toString());
      formData.append("end_time", values.end_time?.toISOString() || "");
      formData.append("category", values.category);
      formData.append("quantity", String(values.quantity || 1));
      formData.append("images", JSON.stringify(values.images));
      formData.append("video", values.video || "");

      if (values.parcel_id) {
        formData.append("parcel_id", values.parcel_id);
      }
      if (values.current_price !== undefined) {
        formData.append("current_price", values.current_price.toString());
      }

      await auctionService.updateAuction(id, formData);

      setSuccess("Auction updated successfully!");
      setTimeout(() => navigate("/admin/live"), 2000);
    } catch (err) {
      setError("Failed to update auction. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!item) return <Alert severity="error">Auction not found</Alert>;

  const initialValues: AuctionFormValues = {
    title: item.title,
    description: item.description,
    starting_price: item.starting_price,
    min_increment: item.min_increment,
    end_time: item.end_time ? new Date(item.end_time) : null,
    category: item.category ?? "",
    quantity: item.quantity ?? 1,
    images: Array.isArray(item.images) ? item.images : [],
    video: item.video,
    parcel_id: item.parcel_id,
    current_price: item.current_price,
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Edit Auction (Admin)
      </Typography>
      <AuctionForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        isEdit={true}
        loading={loading}
        error={error}
        success={success}
        gisProperties={gisProperties}
      />
    </Box>
  );
};

export default EditAuctionAdmin;
